import {
  Center,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldMapsLayout } from '../../../../components/field-maps-layout/field-maps-layout';
import SelectMapType from '../../../../components/select-map-type/select-map-type';
import useSharedFieldMaps from '../../../../hooks/use-shared-field-maps/shared-field-maps';
import { MAP_DISPLAY_TYPES } from '../compare-maps/page';

import { FORM_STATUS } from '@advices/data-access';
import {
  formatDate,
  layersOptions,
  PRESCRIPTION_FROM_TYPES,
} from '@data-access';
import {
  GradientLegend,
  LegendControl,
  PfMap,
  PlotsLayer,
  SelectImageSlider,
  SwitchControl,
  useWmsLayerInfo,
  WmsLayer,
} from '@map';
import { LineAreaSatChart, TsrButtonLink } from '@ui';

interface SatelliteMapProps {
  type: MAP_DISPLAY_TYPES;
}

export function SatelliteMap({ type }: SatelliteMapProps) {
  const { t } = useTranslation();
  const { field, compare, fieldId, queries, handleCompareChange } =
    useSharedFieldMaps({ type });
  const viewport = useRef<HTMLDivElement>(null);
  const [showGraph, setShowGraph] = useState(false);

  const [heatmapType, setHeatmapType] = useState('NDVI');
  const [selectedHeatmap, setSelectedHeatmap] = useState<string | null>(null);

  // Fetch maps data using react-query
  const { data } = useQuery({
    ...queries.heatmapsSummary({
      fieldId: Number(fieldId),
      heatmapType: heatmapType,
    }),
    refetchInterval(query) {
      return query.state.data?.status !== FORM_STATUS.FINISHED ? 5000 : false;
    },
  });

  // Find the currently selected map
  const selectedMap = data?.data.find(
    (map) => map.id?.toString() === selectedHeatmap,
  );

  const heatmapData = data?.data.find(
    (heatmap) => heatmap.id.toString() === selectedHeatmap,
  );

  const { handleWmsLayerClick, wmsInfoPopup } = useWmsLayerInfo({
    imageryIndex: heatmapType,
    wmsSrc: heatmapData?.url,
  });

  // Automatically set the first heatmap option if none is selected
  useEffect(() => {
    if (data?.options?.length && !selectedHeatmap) {
      setSelectedHeatmap(data.options[0].value);
    }
  }, [data, selectedHeatmap]);

  const handleSelectImageChange = (value: string) => {
    setSelectedHeatmap(value);
    scrollGraphTo(value);
  };

  const scrollGraphTo = useCallback(
    (id: string) => {
      if (!data?.graphData) return;
      const index = data?.graphData.findIndex((d) => d.id === id);
      if (index !== -1) {
        viewport.current?.scrollTo({
          left: index * 150,
          behavior: 'smooth',
        });
      }
    },
    [data?.graphData],
  );

  // Render the map and heatmap slider if status is FINISHED
  const renderMapContent = () => {
    if (data?.status === FORM_STATUS.FINISHED && selectedMap) {
      return (
        <Stack gap={0} h="100%">
          <PfMap onClick={handleWmsLayerClick} zoomToGeojson={field.data}>
            {wmsInfoPopup}
            {/* <ImageLayer
              coordinates={data?.corners_coords}
              imageUrl={selectedMap.url}
            /> */}
            <WmsLayer url={selectedMap.url} />
            <PlotsLayer
              data={{ type: 'FeatureCollection', features: [field.data] }}
              outline
            />
            {(heatmapType === 'LAI' || heatmapType === 'NDVI') &&
            data.legend ? (
              <LegendControl>
                <GradientLegend legend={data.legend} />
              </LegendControl>
            ) : null}
            <SwitchControl
              checked={showGraph}
              disabled={data.graphData.length === 0}
              label={t('common.show_graph')}
              onChange={setShowGraph}
            />
          </PfMap>
          <SelectImageSlider
            key={heatmapType + data?.options?.length}
            onChange={handleSelectImageChange}
            options={
              data?.options.map((option) => ({
                ...option,
                manual_check: false,
              })) || []
            }
            value={selectedHeatmap || ''}
          />
          {showGraph && data.graph && data.graphData.length ? (
            <ScrollArea
              flex="0 0 auto"
              h={250}
              scrollbars={'x'}
              viewportRef={viewport}
            >
              <LineAreaSatChart
                data={data.graphData}
                domain={data.graph.domain}
                harvestDate={data.harvestDate}
                previousHarvestDate={formatDate({
                  date: data.previous_crop_harvest_date.toLocaleString(),
                  format: 'display',
                })}
                selectedHeatmap={selectedHeatmap}
                setSelectedHeatmap={setSelectedHeatmap}
                sowingDate={data.sowingDate}
              />
            </ScrollArea>
          ) : null}
        </Stack>
      );
    }
    return null;
  };

  // Render the loading state if status is PENDING
  const renderLoadingState = () => {
    if (!data || data?.status === FORM_STATUS.PENDING) {
      return (
        <Center h="100%" p="xl">
          <Stack align="center">
            <Text fw="bold" size="xl" ta="center">
              {t('Nous sommes en train de préparer vos cartes')}
            </Text>
            <Loader />
          </Stack>
        </Center>
      );
    }
    return null;
  };

  return (
    <FieldMapsLayout
      content={
        <>
          {renderMapContent()}
          {renderLoadingState()}
        </>
      }
      header={
        <Group h="100%" justify="space-between" p="md">
          <Group>
            {type !== 'single' && (
              <SelectMapType
                maps={field.data.properties.maps}
                onChange={handleCompareChange}
                value={type === 'compare-left' ? compare?.[0] : compare?.[1]}
              />
            )}
            <Select
              data={layersOptions(t)}
              disabled={data?.status !== FORM_STATUS.FINISHED}
              onChange={(v) => setHeatmapType(v ?? 'LAI')}
              value={heatmapType}
            />
          </Group>
          <Group>
            <TsrButtonLink
              disabled={heatmapType === 'RGB' || selectedHeatmap === null}
              params={{ fieldId }}
              search={
                selectedMap?.id
                  ? {
                      mapType: PRESCRIPTION_FROM_TYPES.SATELLITE,
                      heatmapId: selectedMap.id,
                      heatmapLayer: heatmapType,
                    }
                  : {}
              }
              to="/fields/$fieldId/maps/prescriptions/create"
            >
              {t('prescriptions.createPrescriptionTitle')}
            </TsrButtonLink>
          </Group>
        </Group>
      }
    />
  );
}

export default SatelliteMap;
